import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useGameStore } from "../../store/gameStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Dices, MessageCircleWarning } from "lucide-react";
import TriggerBadge from "./TriggerBadge";

export default function ObstacleTriggerModal({ gameId, character, myMemberId, myMemberName, isGm }) {
  const pending = useGameStore((s) => s.pendingObstacleTrigger);
  const setPending = useGameStore((s) => s.setPendingObstacleTrigger);
  const sendWs = useGameStore((s) => s.sendWs);
  const [stats, setStats] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [talkedToGm, setTalkedToGm] = useState(false);

  const isMine = pending && pending.member_id === myMemberId;

  useEffect(() => {
    setAcknowledged(false);
    setTalkedToGm(false);
    if (isMine && character?.id) {
      api.get(`/characters/${character.id}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    }
  }, [pending?.obstacle_id]);

  useEffect(() => {
    if (pending && isGm && !isMine) {
      toast.info(`${pending.token_name || "A player"} triggered: ${pending.label}`);
    }
  }, [pending?.obstacle_id, pending?.member_id]);

  if (!isMine) return null;

  const close = () => {
    setPending(null);
    setResult(null);
    setStats(null);
    setAcknowledged(false);
    setTalkedToGm(false);
  };

  const talkToGm = (rollInfo) => {
    sendWs({ type: "talk_to_gm_ping", data: { member_id: myMemberId, member_name: myMemberName, label: pending.label, roll_result: rollInfo || null } });
    setTalkedToGm(true);
    toast.success("The GM has been notified — they'll narrate what happens next.");
  };

  const doRoll = async () => {
    if (!character?.id) {
      toast.error("Create a character first to roll");
      return;
    }
    setRolling(true);
    const gmModifier = pending.gm_modifier || 0;
    const gmSuffix = gmModifier ? ` (GM adjustment ${gmModifier > 0 ? "+" : ""}${gmModifier})` : "";
    try {
      let total;
      const skillMod = pending.skill && stats?.skills?.[pending.skill]?.modifier;
      if (typeof skillMod === "number") {
        const { data } = await api.post(`/characters/${character.id}/roll`, { game_id: gameId, label: `${pending.label} (${pending.skill})${gmSuffix}`, modifier: skillMod + gmModifier });
        total = data.roll_breakdown.total;
      } else {
        const { data } = await api.post(`/characters/${character.id}/paper-roll`, { game_id: gameId, die: "1d20", bonuses: gmModifier ? [{ label: "GM Adjustment", value: gmModifier }] : [], label: `${pending.label}${gmSuffix}` });
        total = data.roll_breakdown.total;
      }
      const passed = total >= pending.dc;
      const { data: resolveData } = await api.post(`/games/${gameId}/obstacles/${pending.obstacle_id}/resolve`, { passed, token_id: pending.token_id, side: pending.side || "a" });
      setResult({ total, pass: passed, effect: resolveData?.effect_result });
      if (resolveData?.spawned) {
        toast.success(`${resolveData.spawned.name} appears!`);
      }
    } catch {
      toast.error("Roll failed");
    } finally {
      setRolling(false);
    }
  };

  return (
    <>
      {!acknowledged && (
        <TriggerBadge testId="obstacle-trigger-badge" label={pending.label} onClick={() => setAcknowledged(true)} />
      )}
      <Dialog open={acknowledged} onOpenChange={(v) => !v && close()}>
        <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="obstacle-trigger-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{pending.label}</DialogTitle>
            <DialogDescription>
              Roll to beat DC {pending.dc}{pending.skill ? ` (${pending.skill})` : ""}
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="space-y-3">
              <p className={`text-2xl font-display ${result.pass ? "text-[#4d7c0f]" : "text-[#D90429]"}`} data-testid="obstacle-roll-result">
                Rolled {result.total} — {result.pass ? "Success!" : "Failed!"}
              </p>
              {result.effect?.message && (
                <p data-testid="obstacle-effect-message" className="text-sm italic text-[#f1e0c5]/80 bg-black/30 border border-white/10 rounded-lg p-2">
                  {result.effect.message}
                </p>
              )}
              {result.effect?.damage && (
                <p data-testid="obstacle-effect-damage" className="text-sm text-[#D90429]">
                  Took {result.effect.damage.final} {result.effect.damage.type || ""} damage
                  {result.effect.damage.note ? ` (${result.effect.damage.note}, rolled ${result.effect.damage.raw})` : ""}
                  {result.effect.damage.hp_after != null ? ` — HP now ${result.effect.damage.hp_after}` : ""}
                </p>
              )}
              {result.effect?.pushed && (
                <p data-testid="obstacle-effect-pushed" className="text-sm text-[#D4AF37]">
                  Pushed {result.effect.pushed.feet}ft {result.effect.pushed.direction}!
                </p>
              )}
              {result.effect?.conditions_applied?.length > 0 && (
                <p data-testid="obstacle-effect-conditions" className="text-xs text-[#D4AF37]">
                  Conditions applied: {result.effect.conditions_applied.join(", ")}
                </p>
              )}
              <div className="flex gap-2">
                <Button data-testid="obstacle-okay-button" onClick={close} className="flex-1 rounded-full bg-[#D90429] hover:bg-[#b8031f]">Okay</Button>
                <Button data-testid="obstacle-talk-to-gm-after-roll-button" onClick={() => talkToGm({ total: result.total, pass: result.pass })} variant="secondary" className="flex-1 rounded-full gap-2">
                  <MessageCircleWarning className="w-4 h-4" /> Talk to GM
                </Button>
              </div>
            </div>
          ) : talkedToGm ? (
            <div className="space-y-3 text-center py-2">
              <MessageCircleWarning className="w-8 h-8 text-[#D4AF37] mx-auto animate-pulse" />
              <p className="text-sm text-[#f1e0c5]/70">Waiting on the GM to narrate what happens…</p>
              <Button data-testid="obstacle-close-button" onClick={close} variant="secondary" className="w-full rounded-full">Close</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {!character?.id && <p className="text-xs text-[#f1e0c5]/50">Create a character to roll for this check.</p>}
              {pending.gm_modifier ? (
                <p data-testid="obstacle-gm-modifier-note" className="text-xs text-[#D4AF37]">
                  The GM has adjusted this roll by {pending.gm_modifier > 0 ? "+" : ""}{pending.gm_modifier}
                </p>
              ) : null}
              <Button data-testid="obstacle-roll-button" onClick={doRoll} disabled={rolling || !character?.id} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
                <Dices className="w-4 h-4" /> Roll Save
              </Button>
              <Button data-testid="obstacle-talk-to-gm-button" onClick={() => talkToGm()} disabled={rolling} variant="secondary" className="w-full rounded-full gap-2">
                <MessageCircleWarning className="w-4 h-4" /> Talk to GM
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
